'use client';

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Searchable IANA timezone picker — replaces the free-text "type the full zone"
 * input. Lists every zone from `Intl.supportedValuesOf('timeZone')` (≈418),
 * filterable by city/region, each row annotated with its current UTC offset.
 * Falls back to a curated short list on the rare engine without supportedValuesOf.
 *
 * Controlled: stores the raw IANA id (e.g. "America/Whitehorse") so the value is
 * exactly what the scheduler interprets the cron in.
 */

// Used only when Intl.supportedValuesOf is unavailable (very old engines).
const FALLBACK_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Whitehorse',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

function getZones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    const list = fn?.('timeZone');
    if (Array.isArray(list) && list.length > 0) return list;
  } catch {
    /* fall through */
  }
  return FALLBACK_ZONES;
}

/** Current UTC offset for a zone, e.g. "UTC-7", "UTC+5:30", "UTC". */
function offsetLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    return raw.replace('GMT', 'UTC') || 'UTC';
  } catch {
    return '';
  }
}

const prettyName = (tz: string) => tz.replace(/_/g, ' ');

interface TimezoneSelectProps {
  value: string;
  onChange: (tz: string) => void;
  className?: string;
}

export function TimezoneSelect({ value, onChange, className }: TimezoneSelectProps) {
  const [open, setOpen] = useState(false);

  // Compute the zone list + offsets once (offsets are "now"-relative but stable
  // enough for a picker; recomputing per render would be wasteful for ~418 zones).
  const zones = useMemo(() => {
    const list = getZones();
    return list.map((tz) => ({ tz, label: prettyName(tz), offset: offsetLabel(tz) }));
  }, []);

  const current = value?.trim();
  const currentOffset = useMemo(() => (current ? offsetLabel(current) : ''), [current]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className="flex items-center gap-2 truncate">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
            {current ? (
              <span className="truncate">
                {prettyName(current)}
                {currentOffset ? <span className="text-muted-foreground"> · {currentOffset}</span> : null}
              </span>
            ) : (
              <span className="text-muted-foreground">Select timezone…</span>
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search city or region…" />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {zones.map(({ tz, label, offset }) => (
                <CommandItem
                  key={tz}
                  // cmdk filters on `value`; use the space-normalized label so
                  // "buenos aires" matches "America/Argentina/Buenos_Aires".
                  value={label}
                  onSelect={() => {
                    onChange(tz);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', current === tz ? 'opacity-100' : 'opacity-0')} />
                  <span className="flex-1 truncate">{label}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{offset}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
